/**
 * Format a number with thousand separators using dots
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @param hideZeroDecimals - Hide decimal part if it's all zeros (default: true)
 * @returns Formatted string with dots as thousand separators (e.g., "5.123.124,50" or "5.123.124")
 */
export const formatNumber = (value: number | string, decimals: number = 2, hideZeroDecimals: boolean = true): string => {
  // Handle edge cases
  if (value === null || value === undefined) {
    return '0';
  }

  // Convert to number if string (e.g., from Decimal serialization)
  let numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return '0';
  }

  // Round to avoid floating point precision issues
  numValue = Math.round(numValue * 100) / 100;

  // Split into integer and decimal parts
  const fixedValue = numValue.toFixed(decimals);
  const [integerPart, decimalPart] = fixedValue.split('.');

  // Add dots as thousand separators
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

  // Return without decimals if they're all zeros and hideZeroDecimals is true
  if (hideZeroDecimals && decimals > 0 && parseInt(decimalPart) === 0) {
    return formattedInteger;
  }

  // Return with decimal part if needed
  return decimals > 0 ? `${formattedInteger},${decimalPart}` : formattedInteger;
};

/**
 * Format a currency amount with thousand separators and currency symbol
 * @param value - The amount to format
 * @param currency - Currency code (e.g., "USD", "EUR")
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string (e.g., "5.123.124,50 USD")
 */
export const formatCurrency = (value: number | string, currency: string, decimals: number = 2): string => {
  const formattedNumber = formatNumber(value, decimals);
  return `${formattedNumber} ${currency}`;
};

export const getMonthDateRange = (selectedDate: Date): { startDate: string; endDate: string } => {
  const year = selectedDate.getFullYear();
  const month = selectedDate.getMonth();

  // First day of the month
  const startDate = new Date(year, month, 1);

  // Last day of the month - using day 0 of next month gives us last day of current month
  const endDate = new Date(year, month + 1, 0);

  // Format as YYYY-MM-DD using local date parts (not toISOString which converts to UTC)
  const pad = (n: number) => n.toString().padStart(2, '0');
  return {
    startDate: `${startDate.getFullYear()}-${pad(startDate.getMonth() + 1)}-${pad(startDate.getDate())}`,
    endDate: `${endDate.getFullYear()}-${pad(endDate.getMonth() + 1)}-${pad(endDate.getDate())}`
  };
};
